import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Check, X, RotateCcw, Sparkles, Share2, Lightbulb, Timer, Rocket, Zap, Hourglass } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import Confetti from "@/components/Confetti";
import AnimatedNumber from "@/components/AnimatedNumber";
import ShareCardDialog from "@/components/ShareCard";

/** Pace tiers — based on average seconds per question on this quiz attempt. */
const PACE_TIERS = [
  { id: "lightning", label: "Lightning", max: 15, color: "#FCA5A5", glow: "rgba(252,165,165,0.55)", Icon: Rocket,    blurb: "Insanely fast — verify accuracy on tricky topics." },
  { id: "quick",     label: "Quick",     max: 30, color: "#5AC8FA", glow: "rgba(90,200,250,0.55)",  Icon: Zap,       blurb: "Sharp and decisive. Great exam pace." },
  { id: "steady",    label: "Steady",    max: 60, color: "#B8A4FF", glow: "rgba(184,164,255,0.55)", Icon: Timer,     blurb: "Measured. Solid for hard sections." },
  { id: "thorough",  label: "Thorough",  max: Infinity, color: "#7C5CFF", glow: "rgba(124,92,255,0.55)", Icon: Hourglass, blurb: "Careful pacing — try a timed retry to push speed." },
];

function paceTierFor(avgSeconds) {
  return PACE_TIERS.find((t) => avgSeconds < t.max) || PACE_TIERS[PACE_TIERS.length - 1];
}
function formatMMSS(totalSeconds) {
  const s = Math.max(0, Math.round(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

/** Standalone live-ticking timer chip — owns its own state so the parent does not
 *  re-render every second just to keep the digits fresh. */
function LiveTimer({ startedAtMs }) {
  const [elapsedSec, setElapsedSec] = useState(() => Math.max(0, (Date.now() - startedAtMs) / 1000));
  useEffect(() => {
    const t = setInterval(() => {
      setElapsedSec((Date.now() - startedAtMs) / 1000);
    }, 1000);
    return () => clearInterval(t);
  }, [startedAtMs]);
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.06] tabular-nums"
      data-testid="quiz-timer"
      title="Time on this attempt"
    >
      <Timer size={11} className="text-[#B8A4FF]" /> {formatMMSS(elapsedSec)}
    </span>
  );
}

export default function QuizRunner({ subcourseId, onComplete }) {
  const { refresh } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [hints, setHints] = useState({}); // { [q_index]: [labels eliminated] }
  const [hintBusy, setHintBusy] = useState(false);

  // Pace tracking — refs avoid re-render churn on every tick.
  const startedAtRef = useRef(null);     // ms timestamp when the quiz began
  const lastShownRef = useRef(null);     // ms timestamp when current question was first shown
  const perQuestionRef = useRef({});     // { [qIndex]: totalSecondsSpent }
  const [startedAt, setStartedAt] = useState(null); // mirrored into state so LiveTimer can subscribe

  const load = () => {
    setIdx(0);
    setAnswers({});
    setResult(null);
    setHints({});
    perQuestionRef.current = {};
    const now = Date.now();
    startedAtRef.current = now;
    lastShownRef.current = now;
    setStartedAt(now);
    api.get(`/subcourses/${subcourseId}/quiz`).then((r) => setQuiz(r.data));
  };
  useEffect(() => { load(); }, [subcourseId]);

  // When the user moves to a different question, accumulate elapsed time on the prior one.
  useEffect(() => {
    if (!quiz) return;
    lastShownRef.current = Date.now();
    return () => {
      if (lastShownRef.current != null) {
        const dt = (Date.now() - lastShownRef.current) / 1000;
        perQuestionRef.current[idx] = (perQuestionRef.current[idx] || 0) + dt;
      }
    };
  }, [idx, quiz]);

  if (!quiz) return <div className="h-96 shimmer rounded-2xl" />;

  const q = quiz.questions[idx];
  const totalQ = quiz.questions.length;
  const setAns = (val) => setAnswers((a) => ({ ...a, [idx]: val }));

  const useHint = async () => {
    if (q.type !== "mcq" || hints[idx]) return;
    setHintBusy(true);
    try {
      const { data } = await api.post("/gems/redeem/quiz-hint", { sc_id: subcourseId, question_index: idx });
      setHints((h) => ({ ...h, [idx]: data.eliminate || [] }));
      await refresh();
      toast.success("Hint used — 2 wrong options crossed out");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't use hint");
    } finally {
      setHintBusy(false);
    }
  };

  const submit = async () => {
    // Flush current question's elapsed time.
    if (lastShownRef.current != null) {
      const dt = (Date.now() - lastShownRef.current) / 1000;
      perQuestionRef.current[idx] = (perQuestionRef.current[idx] || 0) + dt;
      lastShownRef.current = null;
    }
    const totalElapsed = startedAtRef.current ? (Date.now() - startedAtRef.current) / 1000 : 0;
    const totalQ_ = quiz?.questions?.length || 1;
    const avg = totalElapsed / totalQ_;

    setSubmitting(true);
    const payload = { answers: Object.entries(answers).map(([i, a]) => ({ question_index: Number(i), answer: a })) };
    try {
      const { data } = await api.post(`/subcourses/${subcourseId}/quiz/submit`, payload, { timeout: 90000 });
      setResult({ ...data, _elapsed: totalElapsed, _avg: avg, _perQuestion: { ...perQuestionRef.current } });
      refresh();
      onComplete && onComplete();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) return <QuizResult result={result} onRetry={load} />;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-3 text-[12px] text-[#B0AEBC]">
        <span className="font-semibold">Question {idx + 1} of {totalQ}</span>
        <div className="flex items-center gap-3">
          {startedAt != null && <LiveTimer startedAtMs={startedAt} />}
          <span className="tabular-nums">{Object.keys(answers).length} answered</span>
        </div>
      </div>
      <div className="h-1.5 progress-track mb-7">
        <motion.div
          className="h-full progress-fill"
          initial={{ width: 0 }}
          animate={{ width: `${((idx + 1) / totalQ) * 100}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.article
          key={idx}
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -16 }}
          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
          className="surface p-7 md:p-9"
          data-testid="quiz-card"
        >
          <div className="flex items-center gap-1.5 mb-5">
            <span className="chip chip-blue">{q.type === "mcq" ? "Multiple choice" : q.type === "tf" ? "True / False" : "Short answer"}</span>
            <span className={`chip ${q.difficulty === "easy" ? "chip-green" : q.difficulty === "medium" ? "chip-orange" : "chip-pink"} capitalize`}>{q.difficulty}</span>
          </div>
          <h3 className="text-[22px] md:text-[28px] font-semibold tracking-tight leading-tight mb-7">{q.text}</h3>

          {q.type === "mcq" && (
            <>
              <div className="space-y-2" data-testid="mcq-options">
                {q.options.map((o) => {
                  const sel = answers[idx] === o.label;
                  const eliminated = (hints[idx] || []).includes(o.label);
                  return (
                    <motion.button
                      key={o.label}
                      onClick={() => !eliminated && setAns(o.label)}
                      whileHover={!eliminated ? { x: 3 } : {}}
                      data-testid={`mcq-option-${o.label}`}
                      disabled={eliminated}
                      className={`w-full text-left p-4 rounded-xl border transition-all flex items-center gap-3.5 ${
                        eliminated
                          ? "border-white/5 bg-white/[0.02] opacity-40 line-through cursor-not-allowed"
                          : sel
                          ? "border-[#7C5CFF] bg-gradient-to-r from-[#7C5CFF]/15 to-[#5AC8FA]/8 shadow-[0_0_30px_-8px_rgba(124,92,255,0.55)]"
                          : "border-white/8 hover:border-white/20 bg-white/3"
                      }`}
                    >
                      <span
                        className={`w-7 h-7 rounded-lg flex items-center justify-center text-[13px] font-bold shrink-0 transition-all ${
                          sel && !eliminated ? "text-white" : "bg-white/8 text-[#B0AEBC]"
                        }`}
                        style={sel && !eliminated ? { background: "linear-gradient(135deg, #7C5CFF, #5AC8FA)", boxShadow: "0 0 16px rgba(124,92,255,0.55)" } : {}}
                      >
                        {o.label}
                      </span>
                      <span className="text-[15px] text-white">{o.text}</span>
                    </motion.button>
                  );
                })}
              </div>
              {!hints[idx] && (
                <button
                  type="button"
                  onClick={useHint}
                  disabled={hintBusy}
                  data-testid="quiz-hint-btn"
                  className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#B8A4FF] hover:text-white px-3 py-1.5 rounded-full bg-[#B8A4FF]/10 hover:bg-[#B8A4FF]/20 border border-[#B8A4FF]/25 transition-colors"
                >
                  <Lightbulb size={12} /> {hintBusy ? "…" : "Use hint — 10 gems"}
                </button>
              )}
            </>
          )}

          {q.type === "tf" && (
            <div className="grid grid-cols-2 gap-3">
              {[
                { v: "True", color: "#5AC8FA" },
                { v: "False", color: "#B8A4FF" },
              ].map((item) => {
                const sel = answers[idx] === item.v;
                return (
                  <motion.button
                    key={item.v}
                    onClick={() => setAns(item.v)}
                    whileHover={{ y: -3 }}
                    whileTap={{ scale: 0.96 }}
                    data-testid={`tf-${item.v.toLowerCase()}`}
                    className={`py-8 rounded-2xl border-2 text-[18px] font-bold transition-all ${
                      sel
                        ? ""
                        : "border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.03] text-[#1A1A22] dark:text-[#F4F4F5] hover:border-black/20 dark:hover:border-white/20"
                    }`}
                    style={sel ? {
                      borderColor: item.color,
                      background: `${item.color}18`,
                      color: item.color,
                      boxShadow: `0 0 30px -8px ${item.color}`,
                    } : undefined}
                  >
                    {item.v}
                  </motion.button>
                );
              })}
            </div>
          )}

          {q.type === "short" && (
            <textarea
              value={answers[idx] || ""}
              onChange={(e) => setAns(e.target.value)}
              data-testid="short-answer-input"
              placeholder="Type your answer in 1–3 sentences…"
              className="input min-h-[140px] resize-y leading-relaxed"
            />
          )}
        </motion.article>
      </AnimatePresence>

      <div className="flex items-center justify-between mt-6 gap-3">
        <button
          onClick={() => setIdx((i) => Math.max(0, i - 1))}
          disabled={idx === 0}
          data-testid="quiz-prev"
          className="btn btn-ghost disabled:opacity-30"
        >
          ← Previous
        </button>
        {idx < totalQ - 1 ? (
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => setIdx((i) => i + 1)}
            data-testid="quiz-next"
            className="btn btn-secondary"
          >
            Next →
          </motion.button>
        ) : (
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            onClick={submit}
            disabled={submitting || Object.keys(answers).length === 0}
            data-testid="quiz-submit"
            className="btn btn-primary"
          >
            {submitting ? "Grading…" : <><Sparkles size={14} /> Submit</>}
          </motion.button>
        )}
      </div>
    </div>
  );
}

function QuizResult({ result, onRetry }) {
  const { user } = useAuth();
  const [share, setShare] = useState(false);
  const isPerfect = result.score === 100;
  const total = result._elapsed ?? 0;
  const avg = result._avg ?? 0;
  const pace = paceTierFor(avg);
  const PaceIcon = pace.Icon;
  return (
    <div className="max-w-2xl mx-auto relative">
      {isPerfect && <Confetti active count={50} />}
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="surface p-10 mb-8 text-center relative overflow-hidden"
        style={{
          background: isPerfect
            ? "linear-gradient(135deg, #7C5CFF 0%, #5AC8FA 100%)"
            : undefined,
          boxShadow: isPerfect ? "0 30px 80px -20px rgba(124,92,255,0.65), 0 0 60px -10px rgba(90,200,250,0.4)" : undefined,
        }}
        data-testid="quiz-result-card"
      >
        {!isPerfect && (
          <div className="absolute -top-24 -right-24 w-56 h-56 bg-gradient-to-br from-[#7C5CFF]/30 to-[#5AC8FA]/20 blur-3xl rounded-full" />
        )}
        <div className={`relative ${isPerfect ? "text-white" : ""}`}>
          <div className={`eyebrow ${isPerfect ? "text-white/80" : ""}`}>{isPerfect ? "Perfect" : "Result"}</div>
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 220, damping: 18 }}
            className="display-1 mt-3"
          >
            <span className={isPerfect ? "" : "grad-text"} style={{ background: isPerfect ? "transparent" : undefined }}>
              <AnimatedNumber value={result.score} duration={1.4} />
            </span>
            <span className={`text-[40px] ml-1 ${isPerfect ? "text-white/70" : "text-[#8E8B99]"}`}>%</span>
          </motion.div>
          <p className={`text-[15px] mt-3 ${isPerfect ? "text-white/85" : "text-[#B0AEBC]"}`}>
            {result.correct} of {result.total} correct · Mastery now {result.mastery_score}%
          </p>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.5, type: "spring", stiffness: 280, damping: 16 }}
            className={`inline-flex items-center gap-1.5 mt-6 px-4 py-1.5 rounded-full text-[13px] font-bold ${
              isPerfect ? "bg-white/20 text-white backdrop-blur" : "bg-gradient-to-r from-[#7C5CFF] to-[#5AC8FA] text-white"
            }`}
            style={!isPerfect ? { boxShadow: "0 0 24px -4px rgba(124,92,255,0.55)" } : undefined}
          >
            <Sparkles size={13} /> +{result.xp_awarded} XP
          </motion.div>

          {/* Pace summary — based on average seconds per question this attempt */}
          {avg > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65, duration: 0.4 }}
              className={`mt-5 inline-flex items-center gap-2.5 px-4 py-2 rounded-xl ${
                isPerfect ? "bg-white/15 backdrop-blur border border-white/15" : "bg-white/[0.04] border border-white/[0.08]"
              }`}
              style={{ boxShadow: `inset 0 0 0 1px ${pace.color}33, 0 0 22px -10px ${pace.glow}` }}
              data-testid="quiz-pace-badge"
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                style={{ background: `${pace.color}22`, boxShadow: `0 0 14px -2px ${pace.glow}` }}
              >
                <PaceIcon size={15} style={{ color: pace.color }} />
              </div>
              <div className="text-left">
                <div className={`text-[12.5px] font-bold uppercase tracking-[0.14em] ${isPerfect ? "text-white" : ""}`} style={!isPerfect ? { color: pace.color } : undefined}>
                  {pace.label} pace
                </div>
                <div className={`text-[11.5px] tabular-nums ${isPerfect ? "text-white/80" : "text-[#8E8B99]"}`}>
                  {formatMMSS(total)} total · {avg.toFixed(1)}s / question
                </div>
              </div>
            </motion.div>
          )}

          <div className="mt-7 flex items-center justify-center gap-2">
            <button
              onClick={onRetry}
              data-testid="quiz-retry-btn"
              className={`px-4 py-2 rounded-full text-[13px] font-semibold flex items-center gap-2 transition-colors ${
                isPerfect ? "bg-white/20 text-white hover:bg-white/30 backdrop-blur" : "bg-white/8 text-white hover:bg-white/15"
              }`}
            >
              <RotateCcw size={13} /> Retry
            </button>
            {result.score >= 80 && (
              <button
                onClick={() => setShare(true)}
                data-testid="quiz-share-btn"
                className={`px-4 py-2 rounded-full text-[13px] font-semibold flex items-center gap-2 transition-colors ${
                  isPerfect ? "bg-white/20 text-white hover:bg-white/30 backdrop-blur" : "bg-white/8 text-white hover:bg-white/15"
                }`}
              >
                <Share2 size={13} /> Share
              </button>
            )}
          </div>
        </div>
      </motion.div>

      <ShareCardDialog
        open={share}
        onClose={() => setShare(false)}
        type="score"
        data={{ value: result.score, sub: `${result.correct} of ${result.total} correct` }}
        user={user}
      />

      <h3 className="text-[18px] font-semibold mb-4">Review</h3>
      <div className="space-y-2.5">
        {result.results.map((r, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className="surface p-4"
            data-testid={`quiz-review-${i}`}
          >
            <div className="flex items-start gap-3">
              <div className="shrink-0 mt-0.5">
                {r.marks >= 1 ? (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#5AC8FA]/15 to-[#5AC8FA]/15 flex items-center justify-center">
                    <Check size={15} className="text-[#5AC8FA]" strokeWidth={2.6} />
                  </div>
                ) : r.marks > 0 ? (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#B8A4FF]/15 to-[#7C5CFF]/15 flex items-center justify-center text-[#B8A4FF] text-[11px] font-bold">
                    ½
                  </div>
                ) : (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#7C5CFF]/15 to-[#B8A4FF]/15 flex items-center justify-center">
                    <X size={15} className="text-[#7C5CFF]" strokeWidth={2.6} />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-medium leading-snug">{r.question}</p>
                <div className="text-[12px] text-[#B0AEBC] mt-1.5">
                  Your answer: <span className="text-white">{r.your_answer || "—"}</span>
                  {r.marks < 1 && (
                    <span className="ml-3">
                      Correct: <span className="text-[#5AC8FA] font-semibold">{r.correct_answer}</span>
                    </span>
                  )}
                </div>
                {r.explanation && (
                  <p className="text-[12px] text-[#8E8B99] mt-2 leading-relaxed italic">{r.explanation}</p>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <button onClick={onRetry} data-testid="quiz-retry" className="btn btn-secondary mt-7">
        <RotateCcw size={13} /> Retake quiz
      </button>
    </div>
  );
}
